import { ChangeEvent, useCallback, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Search } from "lucide-react";
import { Input } from "./ui/input";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/utils/axiosInterceptor";
import type { ConsolidateTaxEntry } from "@/utils/consolidationTax";
import { Button } from "./ui/button";
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  Row,
  RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";
import { Checkbox } from "./ui/checkbox";
import DataTable from "./ui/data-table";
import { invoiceStatus, invoiceTypes } from "@/constants/invoiceTypes";
import { Label } from "./ui/label";
import { endOfDay, format, startOfDay } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

type SearchInvoiceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectInvoice: (invoice: InvoiceList[]) => void;
};

export type InvoiceList = {
  id: string;
  codeNumber: string; // Max 50 characters
  invoiceDate: Date;
  docDate: Date | null;
  invoiceTime: Date;
  // supplier information
  supplierCode: string; // UUID
  supplierName: string | null;
  // buyer information
  buyerCode: string; // UUID
  buyerName: string | null;
  typeCode: "01" | "02" | "03" | "04" | "11" | "12" | "13" | "14" | "01A";
  postReferenceNumber: string | null;
  totalExcludingTax: number;
  totalTaxAmount: number;
  totalPayableAmount: number;
  uuid: string | null;
  status?: string | null;
  emailStatus?: string | null;
  isConsolidatePickable: boolean;
  isConsolidated: boolean;
  currencyCode: string;
  totalTaxAmountPerTaxType: string | ConsolidateTaxEntry[] | null;
};

export interface DateFilterValue {
  fromDate: string;
  toDate: string;
}

const columns: ColumnDef<InvoiceList>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
  },
  {
    accessorKey: "typeCode",
    header: "Invoice Type",
    cell: ({ row }) => {
      const invoiceTypeMapping = new Map(
        invoiceTypes.map((t) => [t.Code, t.Description]),
      );
      return (
        <span className="font-semibold">
          {`${row.original.typeCode} - ${invoiceTypeMapping.get(
            row.original.typeCode,
          )}`}
        </span>
      );
    },
  },
  {
    accessorKey: "codeNumber",
    header: "Invoice Number",
  },
  {
    accessorKey: "postReferenceNumber",
    header: "POS Reference Number",
  },
  { accessorKey: "totalPayableAmount", header: "Total Payable Amount (RM)" },
  {
    accessorKey: "invoiceDate",
    header: "Issuance Date",
    enableHiding: true,
    cell: ({ row }) => {
      const date = row.original.invoiceDate;
      try {
        return format(new Date(date), "dd/MM/yyyy");
      } catch {
        return String(date);
      }
    },
    filterFn: (
      row: Row<InvoiceList>,
      columnId: string,
      filterValue: { fromDate: string; toDate: string },
    ) => {
      const invoiceDate = row.getValue(columnId) as Date;
      const { fromDate, toDate } = filterValue;

      let matchesDate = true;
      if (fromDate || toDate) {
        const invoiceDateStart = startOfDay(invoiceDate);
        const fromDateObj = fromDate ? startOfDay(new Date(fromDate)) : null;
        const toDateObj = toDate ? endOfDay(new Date(toDate)) : null;

        if (fromDateObj && invoiceDateStart < fromDateObj) {
          matchesDate = false;
        }
        if (toDateObj && invoiceDateStart > toDateObj) {
          matchesDate = false;
        }
      }
      return matchesDate;
    },
  },
  {
    accessorKey: "docDate",
    header: "Invoice Date",
    enableHiding: true,
    cell: ({ row }) => {
      const date = row.original.docDate ?? row.original.invoiceDate;
      try {
        return format(new Date(date), "dd/MM/yyyy");
      } catch {
        return String(date);
      }
    },
    filterFn: (
      row: Row<InvoiceList>,
      _columnId: string,
      filterValue: { fromDate: string; toDate: string },
    ) => {
      const rawDate = row.original.docDate ?? row.original.invoiceDate;
      const date = startOfDay(new Date(rawDate));
      const { fromDate, toDate } = filterValue;

      let matchesDate = true;
      if (fromDate || toDate) {
        const fromDateObj = fromDate ? startOfDay(new Date(fromDate)) : null;
        const toDateObj = toDate ? endOfDay(new Date(toDate)) : null;

        if (fromDateObj && date < fromDateObj) matchesDate = false;
        if (toDateObj && date > toDateObj) matchesDate = false;
      }
      return matchesDate;
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => (
      <span
        className={`px-3 rounded-full ${
          row.original.status ? "bg-orange-300" : "bg-yellow-300"
        }`}
      >
        {row.original.status ?? "Draft"}
      </span>
    ),
    filterFn: (
      row: Row<InvoiceList>,
      columnId: string,
      filterValue: string,
    ) => {
      const status = row.getValue(columnId) as string | null;
      // If filter is "All", show all rows
      if (filterValue === "") return true;
      // Map null status to "Draft" for comparison
      const rowStatus = status ?? "Draft";
      // Compare filterValue ("null" or "Invalid") with row status
      return filterValue === "null"
        ? rowStatus === "Draft"
        : rowStatus === filterValue;
    },
  },
];

const SearchInvoiceModal = ({
  isOpen,
  onClose,
  onSelectInvoice,
}: SearchInvoiceModalProps) => {
  const { toast } = useToast();
  const { roundTotal } = useDecimalPrecision();
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceList[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [tableInstance, setTableInstance] = useState<ReturnType<
    typeof useReactTable<InvoiceList>
  > | null>(null);

  const { data: invoices = [] } = useQuery({
    queryKey: ["invalidInvoices"],
    queryFn: async () => {
      const response = await api.get<InvoiceList[]>(
        "/invoices?status=null,Invalid&isConsolidatePickable=true",
      );
      return response.data;
    },
    enabled: isOpen,
  });

  const globalFilterFn: FilterFn<InvoiceList> = (
    row: Row<InvoiceList>,
    _columnId: string,
    filterValue: string,
  ) => {
    const searchValue = filterValue.toLowerCase();
    const codeNumber = String(row.original.codeNumber || "").toLowerCase();
    const postReferenceNumber = String(
      row.original.postReferenceNumber || "",
    ).toLowerCase();
    return (
      !searchValue ||
      codeNumber.includes(searchValue) ||
      postReferenceNumber.includes(searchValue)
    );
  };

  //helper to check for prefix number pattern
  const parseCodeNumber = (
    codeNumber: string,
  ): { prefix: string; number: number } | null => {
    const match = codeNumber.match(/^(.+?)(\d+)$/);

    if (!match) return null;

    return {
      prefix: match[1],
      number: parseInt(match[2], 10),
    };
  };

  //helper to create grouped invoices from a range
  const createGroupedInvoices = (range: InvoiceList[]): InvoiceList => {
    const totalExcludingTax = range.reduce(
      (sum, invoice) => sum + Number(invoice.totalExcludingTax),
      0,
    );
    const totalTaxAmount = range.reduce(
      (sum, invoice) => sum + Number(invoice.totalTaxAmount),
      0,
    );
    const totalPayableAmount = range.reduce(
      (sum, invoice) => sum + Number(invoice.totalPayableAmount),
      0,
    );
    const startCodeNumber = range[0].codeNumber;
    const endCodeNumber = range[range.length - 1].codeNumber;
    const combinedCodeNumber =
      startCodeNumber === endCodeNumber
        ? startCodeNumber
        : `${startCodeNumber} - ${endCodeNumber}`;

    // Carry the per-tax-type taxable base
    const taxByType = new Map<
      string,
      { taxAmount: number; taxableAmount: number; hasBase: boolean }
    >();
    for (const invoice of range) {
      const raw = invoice.totalTaxAmountPerTaxType;
      const entries: ConsolidateTaxEntry[] = Array.isArray(raw)
        ? raw
        : typeof raw === "string"
          ? (() => {
              try {
                return JSON.parse(raw);
              } catch {
                return [];
              }
            })()
          : [];
      for (const entry of entries) {
        const existing = taxByType.get(entry.taxType) ?? {
          taxAmount: 0,
          taxableAmount: 0,
          hasBase: false,
        };
        const base = Number(entry.taxableAmount ?? 0);
        taxByType.set(entry.taxType, {
          taxAmount: existing.taxAmount + Number(entry.taxAmount ?? 0),
          taxableAmount: existing.taxableAmount + base,
          hasBase: existing.hasBase || entry.taxableAmount != null,
        });
      }
    }
    // Sorted
    const mergedTaxAmountPerTaxType = Array.from(taxByType.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([taxType, totals]) => ({
        taxType,
        taxAmount: roundTotal(totals.taxAmount),
        ...(totals.hasBase
          ? { taxableAmount: roundTotal(totals.taxableAmount) }
          : {}),
      }));

    return {
      codeNumber: combinedCodeNumber,
      totalExcludingTax: roundTotal(totalExcludingTax),
      totalTaxAmount: roundTotal(totalTaxAmount),
      totalPayableAmount: roundTotal(totalPayableAmount),
      totalTaxAmountPerTaxType: mergedTaxAmountPerTaxType,
      supplierCode: range[0].supplierCode,
      supplierName: range[0].supplierName,
      buyerCode: range[0].buyerCode,
      buyerName: range[0].buyerName,
      status: null,
      typeCode: range[0].typeCode,
      id: range[0].id,
      invoiceDate: range[0].invoiceDate,
      docDate: range[0].docDate,
      invoiceTime: range[0].invoiceTime,
      uuid: range[0].uuid,
      isConsolidatePickable: range[0].isConsolidatePickable,
      isConsolidated: range[0].isConsolidated,
      postReferenceNumber: range[0].postReferenceNumber,
      currencyCode: range[0].currencyCode,
    };
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilter(value);
  };

  const handleFromDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fromDate = e.target.value;
    setColumnFilters((prev) => {
      const existing = prev.find((filter) => filter.id === "invoiceDate");
      const existingValue: DateFilterValue = existing
        ? (existing.value as DateFilterValue)
        : { fromDate: "", toDate: "" };
      return [
        ...prev.filter((filter) => filter.id !== "invoiceDate"),
        { id: "invoiceDate", value: { ...existingValue, fromDate } },
      ];
    });
  };

  const handleToDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const toDate = e.target.value;
    setColumnFilters((prev) => {
      const existing = prev.find((filter) => filter.id === "invoiceDate");
      const existingValue: DateFilterValue = existing
        ? (existing.value as DateFilterValue)
        : { fromDate: "", toDate: "" };
      return [
        ...prev.filter((filter) => filter.id !== "invoiceDate"),
        { id: "invoiceDate", value: { ...existingValue, toDate } },
      ];
    });
  };

  const handleFromDocDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fromDate = e.target.value;
    setColumnFilters((prev) => {
      const existing = prev.find((filter) => filter.id === "docDate");
      const existingValue: DateFilterValue = existing
        ? (existing.value as DateFilterValue)
        : { fromDate: "", toDate: "" };
      return [
        ...prev.filter((filter) => filter.id !== "docDate"),
        { id: "docDate", value: { ...existingValue, fromDate } },
      ];
    });
  };

  const handleToDocDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const toDate = e.target.value;
    setColumnFilters((prev) => {
      const existing = prev.find((filter) => filter.id === "docDate");
      const existingValue: DateFilterValue = existing
        ? (existing.value as DateFilterValue)
        : { fromDate: "", toDate: "" };
      return [
        ...prev.filter((filter) => filter.id !== "docDate"),
        { id: "docDate", value: { ...existingValue, toDate } },
      ];
    });
  };

  const handleRowSelection = (selectedRow: InvoiceList[]) => {
    setSelectedInvoice(selectedRow);
  };

  const handleGroupedInvoicesOnSelect = (selectedInvoices: InvoiceList[]) => {
    const sortedInvoices = selectedInvoices.sort((a, b) =>
      a.codeNumber.localeCompare(b.codeNumber),
    );
    const groupedByPrefix: {
      [prefix: string]: { invoice: InvoiceList; number: number }[];
    } = {};

    sortedInvoices.forEach((invoice) => {
      const match = parseCodeNumber(invoice.codeNumber);
      if (!match) return;

      const { prefix, number } = match;

      if (!groupedByPrefix[prefix]) {
        groupedByPrefix[prefix] = [];
      }
      groupedByPrefix[prefix].push({ invoice, number });
    });

    const result: InvoiceList[] = [];
    for (const prefix in groupedByPrefix) {
      const group = groupedByPrefix[prefix];
      let currentRange: InvoiceList[] = [];
      let prevNumber: number | null = null;

      group.forEach(({ invoice, number }, index) => {
        if (prevNumber === null || number === prevNumber + 1) {
          currentRange.push(invoice);
        } else {
          result.push(createGroupedInvoices(currentRange));
          currentRange = [invoice];
        }
        prevNumber = number;

        if (index === group.length - 1 && currentRange.length > 0) {
          result.push(createGroupedInvoices(currentRange));
        }
      });
    }
    return result;
  };

  const handleSelectAllRow = useCallback(() => {
    if (tableInstance) {
      tableInstance.toggleAllRowsSelected(true); //select all rows across all pages
    }
  }, [tableInstance]);

  const deselectAllRows = useCallback(() => {
    if (tableInstance) {
      tableInstance.toggleAllRowsSelected(false); //deselect all rows across all pages
    }
  }, [tableInstance]);

  //get the filter value for the inputs
  const invoiceDateFilter = columnFilters.find((f) => f.id === "invoiceDate");
  const dateFilterValue = (invoiceDateFilter?.value as DateFilterValue) || {
    fromDate: "",
    toDate: "",
  };
  const docDateColumnFilter = columnFilters.find((f) => f.id === "docDate");
  const docDateFilterValue =
    (docDateColumnFilter?.value as DateFilterValue) || {
      fromDate: "",
      toDate: "",
    };
  const statusFilter = columnFilters.find((f) => f.id === "status");
  const statusFilterValue = (statusFilter?.value as string) || "";

  const handleStatusChange = (selectedStatus: string) => {
    setColumnFilters((prev) => {
      return [
        ...prev.filter((filter) => filter.id !== "status"),
        ...(selectedStatus !== "All"
          ? [{ id: "status", value: selectedStatus }]
          : []),
      ];
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        onInteractOutside={(event) => event.preventDefault()}
        className="max-w-6xl h-fit"
      >
        <DialogHeader>
          <DialogTitle>Invoices</DialogTitle>
          <DialogDescription>
            List of invoices that are eligible to consolidate
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative flex flex-col gap-2">
            <div className="w-full">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Invoice number/POS reference number"
                value={globalFilter}
                onChange={handleSearch}
                className="pl-8 max-w-[70%]"
                aria-label="Search invoices"
              />
            </div>
            <div className="flex w-full justify-between items-center">
              <div className="w-full flex flex-wrap items-center gap-3 text-gray-500">
                <div className="flex gap-1.5 items-center">
                  <Label className="whitespace-nowrap">Invoice Date From</Label>
                  <Input
                    type="date"
                    value={docDateFilterValue.fromDate}
                    onChange={handleFromDocDateChange}
                    aria-label="Invoice Date From"
                  />
                </div>
                <div className="flex gap-1.5 items-center">
                  <Label>To</Label>
                  <Input
                    type="date"
                    value={docDateFilterValue.toDate}
                    onChange={handleToDocDateChange}
                    aria-label="Invoice Date To"
                  />
                </div>
                <div className="flex gap-1.5 items-center">
                  <Label className="whitespace-nowrap">
                    Issuance Date From
                  </Label>
                  <Input
                    type="date"
                    value={dateFilterValue.fromDate}
                    onChange={handleFromDateChange}
                    aria-label="Issuance Date From"
                  />
                </div>
                <div className="flex gap-1.5 items-center">
                  <Label>To</Label>
                  <Input
                    type="date"
                    value={dateFilterValue.toDate}
                    onChange={handleToDateChange}
                    aria-label="Issuance Date To"
                  />
                </div>
                <div className="flex gap-1.5 items-center">
                  <Label>Status</Label>
                  <Select
                    onValueChange={handleStatusChange}
                    value={statusFilterValue}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      {invoiceStatus.map((status) => (
                        <SelectItem key={status.label} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" variant="ghost" onClick={handleSelectAllRow}>
                  Select All Pages
                </Button>
                <Button size="sm" variant="ghost" onClick={deselectAllRows}>
                  Deselect All
                </Button>
              </div>
              <Button
                size="sm"
                onClick={() => {
                  const currencies = [
                    ...new Set(selectedInvoice.map((inv) => inv.currencyCode)),
                  ];
                  if (currencies.length > 1) {
                    toast({
                      title: "Cannot consolidate invoices",
                      description:
                        "All selected invoices must have the same currency. Please select invoices with a single currency only.",
                      variant: "destructive",
                    });
                    return;
                  }
                  onSelectInvoice(
                    handleGroupedInvoicesOnSelect(selectedInvoice),
                  );
                  onClose();
                }}
              >
                Add Invoices
              </Button>
            </div>
          </div>
          <DataTable<InvoiceList>
            data={invoices}
            columns={columns}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            onRowSelect={handleRowSelection}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            globalFilterFn={globalFilterFn}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            onTableInstance={setTableInstance}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchInvoiceModal;
